/**
 * The top bar: where you are, and the few things you always want at hand.
 *
 * The search field is a decoy in the best sense — clicking it opens the
 * command palette rather than filtering in place, because once a product is
 * keyboard-first there should be exactly one way to find anything.
 */
import { Search, Sun, Moon, Monitor, LogOut, Upload, Download, Check } from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useWorkspace, personOf } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { useTheme, type ThemePreference } from '@/hooks/useTheme'
import { Button, Tooltip, Kbd, Avatar, MOD } from '@/components/ui/primitives'
import { cn } from '@/lib/utils'
import { pluralise } from '@/lib/format'
import { PresenceBar } from './Presence'

export function TopBar({ onUpload, onDownload, onSignOut }: {
  onUpload: () => void
  onDownload: () => void
  onSignOut: () => void
}) {
  const user = useWorkspace(s => s.user)
  const roster = useWorkspace(s => s.roster)
  const projects = useWorkspace(s => s.projects)
  const { projectId, openPath, selection, setPaletteOpen } = useUi()

  const project = projects.find(p => p.id === projectId)
  const me = user ?? personOf(roster, 'atharva' as never)

  return (
    <header className="flex h-11 shrink-0 items-center gap-2 border-b border-[var(--border)] bg-app px-3">
      <div className="flex min-w-0 items-center gap-1.5 text-sm">
        {project ? (
          <>
            <span className="font-medium text-[var(--fg)]">{project.name}</span>
            {openPath && (
              <>
                <span className="text-[var(--fg-subtle)]">/</span>
                <span className="truncate text-[var(--fg-muted)]" title={openPath}>{openPath}</span>
              </>
            )}
          </>
        ) : (
          <span className="text-[var(--fg-muted)]">Select a project</span>
        )}
      </div>

      <div className="flex-1" />

      <PresenceBar />
      <div className="mx-1 h-4 w-px bg-[var(--border)]" />

      <button
        onClick={() => setPaletteOpen(true)}
        className="snap group flex h-7 w-64 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-inset)] px-2 text-left text-[var(--fg-subtle)] hover:border-[var(--border-strong)] hover:text-[var(--fg-muted)]"
      >
        <Search className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
        <span className="flex-1 truncate text-sm">Search files, people, actions…</span>
        <Kbd keys={[MOD, 'K']} />
      </button>

      <div className="mx-1 h-4 w-px bg-[var(--border)]" />

      {selection.size > 0 && (
        <span className="tabular mr-1 text-xs text-[var(--fg-muted)]">
          {pluralise(selection.size, 'selected', 'selected')}
        </span>
      )}

      <Tooltip label="Download selection" keys={[MOD, '⇧', 'S']}>
        <Button variant="ghost" size="icon" onClick={onDownload} disabled={selection.size === 0} aria-label="Download selection">
          <Download className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </Tooltip>

      <Tooltip label="Upload files" keys={[MOD, 'U']}>
        <Button variant="ghost" size="icon" onClick={onUpload} disabled={!projectId} aria-label="Upload files">
          <Upload className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </Tooltip>

      <ThemeMenu />

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="snap ml-0.5 rounded-full focus-visible:outline-2" aria-label={`Signed in as ${me.name}`}>
            <Avatar user={me} size={24} />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end" sideOffset={6}
            className="pop-in z-50 min-w-52 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] p-1 shadow-pop"
          >
            <div className="flex items-center gap-2 px-2 py-1.5">
              <Avatar user={me} size={28} />
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{me.name}</div>
                <div className="truncate text-xs text-[var(--fg-subtle)]">{me.email}</div>
              </div>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-[var(--border)]" />
            <DropdownMenu.Item
              onSelect={onSignOut}
              className="snap flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-[var(--bg-hover)]"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} /> Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </header>
  )
}

const THEMES: { id: ThemePreference; label: string; icon: typeof Sun }[] = [
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'dark', label: 'Dark', icon: Moon },
  { id: 'system', label: 'System', icon: Monitor },
]

function ThemeMenu() {
  const { theme, preference, setPreference } = useTheme()
  const Icon = preference === 'system' ? Monitor : theme === 'dark' ? Moon : Sun

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label="Appearance">
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end" sideOffset={6}
          className="pop-in z-50 min-w-36 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] p-1 shadow-pop"
        >
          {THEMES.map(option => (
            <DropdownMenu.Item
              key={option.id}
              onSelect={() => setPreference(option.id)}
              className={cn(
                'snap flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-sm outline-none',
                'data-[highlighted]:bg-[var(--bg-hover)]',
              )}
            >
              <option.icon className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span className="flex-1">{option.label}</span>
              {preference === option.id && <Check className="h-3.5 w-3.5" strokeWidth={2} />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
